import React from 'react';
import { Check, X } from 'lucide-react';

const HEADING_FONT = 'Manrope, sans-serif';
const BODY_FONT = 'Inter, sans-serif';

/**
 * Pricing Block Renderer
 * Variants: cards, comparison, simple
 */
export default function PricingRenderer({ props, variant }) {
  const {
    headline = 'Pricing',
    subheadline = '',
    plans = [],
    background_color = '#FAFAF5',
  } = props;

  const headerEl = (
    <div className="text-center mb-12">
      <h2
        className="text-3xl sm:text-4xl font-bold text-stone-900 mb-3"
        style={{ fontFamily: HEADING_FONT }}
      >
        {headline}
      </h2>
      {subheadline && (
        <p
          className="text-lg text-stone-500 max-w-2xl mx-auto"
          style={{ fontFamily: BODY_FONT }}
        >
          {subheadline}
        </p>
      )}
    </div>
  );

  if (!plans.length) {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-6xl mx-auto">
          {headerEl}
          <p className="text-center text-stone-400 text-sm">No pricing plans added yet.</p>
        </div>
      </section>
    );
  }

  // Cards variant
  if (variant === 'cards' || !variant) {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-6xl mx-auto">
          {headerEl}
          <div
            className={`grid grid-cols-1 ${
              plans.length === 2 ? 'md:grid-cols-2 max-w-4xl' : 'md:grid-cols-2 lg:grid-cols-3'
            } gap-6 mx-auto`}
          >
            {plans.map((plan, i) => (
              <div
                key={i}
                className={`relative bg-white rounded-xl border p-8 flex flex-col ${
                  plan.is_popular
                    ? 'border-stone-900 shadow-lg scale-[1.02] z-10'
                    : 'border-stone-200 shadow-sm'
                }`}
              >
                {plan.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span
                      className="inline-block bg-stone-900 text-white text-xs font-semibold px-3 py-1 rounded-full"
                      style={{ fontFamily: BODY_FONT }}
                    >
                      {plan.badge_text || 'Popular'}
                    </span>
                  </div>
                )}
                <div className="mb-6">
                  <h3
                    className="text-lg font-bold text-stone-900 mb-1"
                    style={{ fontFamily: HEADING_FONT }}
                  >
                    {plan.name}
                  </h3>
                  {plan.description && (
                    <p className="text-sm text-stone-400" style={{ fontFamily: BODY_FONT }}>
                      {plan.description}
                    </p>
                  )}
                </div>
                <div className="mb-6">
                  <span
                    className="text-4xl font-bold text-stone-900"
                    style={{ fontFamily: HEADING_FONT }}
                  >
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-stone-400 text-sm ml-1" style={{ fontFamily: BODY_FONT }}>
                      /{plan.period}
                    </span>
                  )}
                </div>
                {plan.features && plan.features.length > 0 && (
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {plan.features.map((feature, fi) => (
                      <li key={fi} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span
                          className="text-sm text-stone-600"
                          style={{ fontFamily: BODY_FONT }}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <a
                  href={plan.cta_url || '#'}
                  className={`block w-full text-center px-6 py-3 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90 ${
                    plan.is_popular
                      ? 'bg-stone-900 text-white'
                      : 'bg-stone-100 text-stone-900 hover:bg-stone-200'
                  }`}
                  style={{ fontFamily: BODY_FONT }}
                >
                  {plan.cta_text || 'Get Started'}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Comparison table variant
  if (variant === 'comparison') {
    // Collect all unique features across plans
    const allFeatures = [];
    const featureSets = plans.map((plan) => new Set(plan.features || []));
    plans.forEach((plan) => {
      (plan.features || []).forEach((f) => {
        if (!allFeatures.includes(f)) allFeatures.push(f);
      });
    });

    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-5xl mx-auto">
          {headerEl}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ fontFamily: BODY_FONT }}>
              <thead>
                <tr>
                  <th className="text-left p-4 text-sm font-semibold text-stone-500 border-b border-stone-200">
                    Feature
                  </th>
                  {plans.map((plan, i) => (
                    <th
                      key={i}
                      className={`text-center p-4 border-b ${
                        plan.is_popular ? 'border-stone-900 bg-stone-50' : 'border-stone-200'
                      }`}
                    >
                      <div
                        className="text-base font-bold text-stone-900 mb-1"
                        style={{ fontFamily: HEADING_FONT }}
                      >
                        {plan.name}
                      </div>
                      <div className="text-2xl font-bold text-stone-900" style={{ fontFamily: HEADING_FONT }}>
                        {plan.price}
                        {plan.period && (
                          <span className="text-sm font-normal text-stone-400">/{plan.period}</span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allFeatures.map((feature, fi) => (
                  <tr key={fi} className="border-b border-stone-100">
                    <td className="p-4 text-sm text-stone-600">{feature}</td>
                    {featureSets.map((fSet, pi) => (
                      <td
                        key={pi}
                        className={`p-4 text-center ${plans[pi].is_popular ? 'bg-stone-50' : ''}`}
                      >
                        {fSet.has(feature) ? (
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-stone-300 mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="p-4" />
                  {plans.map((plan, i) => (
                    <td key={i} className={`p-4 text-center ${plan.is_popular ? 'bg-stone-50' : ''}`}>
                      <a
                        href={plan.cta_url || '#'}
                        className={`inline-block px-6 py-2.5 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90 ${
                          plan.is_popular
                            ? 'bg-stone-900 text-white'
                            : 'bg-stone-100 text-stone-900'
                        }`}
                        style={{ fontFamily: BODY_FONT }}
                      >
                        {plan.cta_text || 'Get Started'}
                      </a>
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>
    );
  }

  // Simple variant (stacked list)
  if (variant === 'simple') {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
        <div className="max-w-2xl mx-auto">
          {headerEl}
          <div className="space-y-6">
            {plans.map((plan, i) => (
              <div
                key={i}
                className={`p-6 rounded-xl border ${
                  plan.is_popular ? 'border-stone-900 bg-white shadow-md' : 'border-stone-200 bg-white'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3
                      className="text-lg font-bold text-stone-900"
                      style={{ fontFamily: HEADING_FONT }}
                    >
                      {plan.name}
                      {plan.is_popular && (
                        <span className="ml-2 inline-block bg-stone-900 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full align-middle">
                          {plan.badge_text || 'Popular'}
                        </span>
                      )}
                    </h3>
                    {plan.description && (
                      <p className="text-sm text-stone-400 mt-0.5" style={{ fontFamily: BODY_FONT }}>
                        {plan.description}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-stone-900" style={{ fontFamily: HEADING_FONT }}>
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className="text-sm text-stone-400">/{plan.period}</span>
                    )}
                  </div>
                </div>
                {plan.features && plan.features.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {plan.features.map((f, fi) => (
                      <span
                        key={fi}
                        className="inline-flex items-center gap-1 text-xs text-stone-500 bg-stone-50 px-2 py-1 rounded"
                        style={{ fontFamily: BODY_FONT }}
                      >
                        <Check className="w-3 h-3 text-green-500" />
                        {f}
                      </span>
                    ))}
                  </div>
                )}
                <a
                  href={plan.cta_url || '#'}
                  className={`inline-block px-5 py-2 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90 ${
                    plan.is_popular
                      ? 'bg-stone-900 text-white'
                      : 'bg-stone-100 text-stone-900'
                  }`}
                  style={{ fontFamily: BODY_FONT }}
                >
                  {plan.cta_text || 'Get Started'}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Fallback to cards
  return (
    <section className="py-16 px-6" style={{ backgroundColor: background_color }}>
      <div className="max-w-6xl mx-auto">
        {headerEl}
        <p className="text-center text-stone-400 text-sm">Unknown pricing variant.</p>
      </div>
    </section>
  );
}
